import { Pencil, Trash2, Pause, Play, Calendar, Tag, Layers } from 'lucide-react';
import type { PromocionVista, ProductoVista, PaqueteVista } from '@shared/types';
import {
  estadoPromocion,
  diasParaVencer,
  diasParaIniciar,
  cuandoAplicaLegible,
  tituloPromocion,
  elegiblesLegibles,
  type EstadoPromo,
} from './promocionHelpers';

/**
 * Sprint 16 — Card de promoción con lenguaje natural y estado real.
 *
 * Reemplaza la fila de tabla densa por una unidad visual que la dueña
 * puede entender de un vistazo:
 *  - Estado prominente arriba: 🟢 ACTIVA AHORA / ⏰ VENCE EN N / ⚪ FUERA HORARIO / 📅 PROGRAMADA / 🔴 VENCIDA / ⏸ PAUSADA.
 *  - Título traducido: "20% de descuento" en lugar de "DESCUENTO_PORCENTAJE 20".
 *  - "En:" con primeros 4 elegibles (+ N más).
 *  - Vigencia + días de la semana en español.
 *  - Toggle Pausar/Reanudar inline (sin abrir form).
 */

interface Props {
  promo: PromocionVista;
  productos: ProductoVista[];
  paquetes: PaqueteVista[];
  moneda: string;
  locale: string;
  onEditar: () => void;
  onEliminar: () => void;
  onToggleActiva: () => void;
}

const STYLE: Record<EstadoPromo, { borde: string; fondo: string; chip: string; label: string }> = {
  activa_hoy:    { borde: 'border-emerald-300', fondo: 'bg-emerald-50',  chip: 'bg-emerald-600 text-white', label: '🟢 ACTIVA AHORA' },
  vence_pronto:  { borde: 'border-amber-400',   fondo: 'bg-amber-50',    chip: 'bg-amber-500 text-white',  label: '⏰ VENCE PRONTO' },
  fuera_horario: { borde: 'border-slate-200',   fondo: 'bg-white',       chip: 'bg-slate-200 text-slate-700', label: '⚪ FUERA DE HORARIO' },
  programada:    { borde: 'border-sky-300',     fondo: 'bg-sky-50',      chip: 'bg-sky-600 text-white',    label: '📅 PROGRAMADA' },
  vencida:       { borde: 'border-red-200',     fondo: 'bg-red-50',      chip: 'bg-red-600 text-white',    label: '🔴 VENCIDA' },
  pausada:       { borde: 'border-slate-300',   fondo: 'bg-slate-100',   chip: 'bg-slate-500 text-white',  label: '⏸ PAUSADA' },
};

export function PromocionCard({
  promo,
  productos,
  paquetes,
  moneda,
  locale,
  onEditar,
  onEliminar,
  onToggleActiva,
}: Props) {
  const estado = estadoPromocion(promo);
  const style = STYLE[estado];
  const titulo = tituloPromocion(promo, productos, moneda, locale);
  const { items, resto } = elegiblesLegibles(promo, productos, paquetes, 4);
  // Sprint 74 — Días Y horas: el horario es el dato que decide si se regala dinero fuera de hora.
  const dias = cuandoAplicaLegible(promo);

  // Anotación de tiempo según estado.
  let tiempoNota = '';
  if (estado === 'vence_pronto') {
    const d = diasParaVencer(promo);
    tiempoNota = d <= 0 ? 'Vence hoy' : `Vence en ${d} día${d === 1 ? '' : 's'}`;
  } else if (estado === 'programada') {
    const d = diasParaIniciar(promo);
    tiempoNota = d <= 0 ? 'Inicia hoy' : `Inicia en ${d} día${d === 1 ? '' : 's'}`;
  } else if (estado === 'fuera_horario') {
    tiempoNota = dias;
  }

  const formatFecha = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: '2-digit' });

  return (
    <div className={`rounded-xl border-2 ${style.borde} ${style.fondo} p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow ${estado === 'vencida' ? 'opacity-75' : ''}`}>
      {/* Chip estado + meta */}
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold tracking-wide ${style.chip}`}>
          {style.label}
        </span>
        {tiempoNota && (
          <span className="text-xs text-slate-600 italic">{tiempoNota}</span>
        )}
      </div>

      {/* Nombre */}
      <div>
        <h3 className="text-lg font-bold leading-tight text-slate-800">{promo.nombre}</h3>
        {promo.descripcion && (
          <p className="text-xs text-slate-500 mt-0.5">{promo.descripcion}</p>
        )}
      </div>

      {/* Título traducido (la "promesa" en lenguaje natural) */}
      <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
        <div className="text-xs uppercase tracking-wide text-slate-600 mb-0.5">La oferta es</div>
        <div className="font-semibold text-slate-800 text-base">💰 {titulo}</div>
      </div>

      {/* Elegibles */}
      <div className="text-sm">
        <div className="flex items-start gap-1.5 text-slate-700">
          <Tag className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-slate-600">En:</div>
            {items.length === 0 ? (
              <div className="text-xs text-slate-400 italic">Sin productos asignados</div>
            ) : (
              <ul className="text-xs text-slate-700 space-y-0.5">
                {items.map((nombre, i) => (
                  <li key={i} className="truncate">• {nombre}</li>
                ))}
                {resto > 0 && (
                  <li className="text-slate-400 italic">+ {resto} más…</li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Vigencia + días */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-start gap-1.5 text-slate-600">
          <Calendar className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-600">Vigencia</div>
            <div>{formatFecha(promo.vigenciaInicio)} → {formatFecha(promo.vigenciaFin)}</div>
          </div>
        </div>
        <div className="flex items-start gap-1.5 text-slate-600">
          <Layers className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-600">Cuándo aplica</div>
            <div>{dias}</div>
          </div>
        </div>
      </div>

      {/* Cantidad mínima, monto mínimo, acumulable */}
      {(promo.cantidadMinima > 1 || promo.montoMinimoPedido || promo.acumulable) && (
        <div className="flex flex-wrap gap-1.5 text-[11px]">
          {promo.cantidadMinima > 1 && (
            <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-700">
              Mín. {promo.cantidadMinima} unidades
            </span>
          )}
          {promo.montoMinimoPedido != null && promo.montoMinimoPedido > 0 && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-800 font-semibold">
              Mín. compra {moneda}{(promo.montoMinimoPedido / 100).toFixed(2)}
            </span>
          )}
          {promo.acumulable && (
            <span className="rounded bg-blue-100 px-2 py-0.5 text-blue-700">
              Acumula con otras promos
            </span>
          )}
        </div>
      )}

      {/* Acciones */}
      <div className="flex gap-1.5 border-t border-slate-200 pt-2 -mx-1">
        <button
          onClick={onToggleActiva}
          className={`btn-ghost flex-1 ${promo.activa ? 'text-amber-700' : 'text-emerald-700'}`}
          title={promo.activa ? 'Pausar (sigue programada pero no aplica)' : 'Reanudar'}
        >
          {promo.activa ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          <span className="text-xs">{promo.activa ? 'Pausar' : 'Reanudar'}</span>
        </button>
        <button onClick={onEditar} className="btn-ghost text-slate-700" aria-label="Editar">
          <Pencil className="h-4 w-4" />
        </button>
        <button onClick={onEliminar} className="btn-ghost text-red-600" aria-label="Eliminar">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
